import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useScroll, useTransform, useSpring } from "framer-motion";
import { Lines, Fade } from "./Reveal";

const PKG_RATIO = 1086 / 1448;
const LABEL = { x: 0.725, y: 0.424, w: 0.362 };
const DISC = 0.6875;
const T = 2.4;
const SLICES = Array.from({ length: 7 }, (_, i) => -T + (2 * T * i) / 6);
const SLICES_M = [-T, 0, T];

const clamp01 = (v) => Math.min(1, Math.max(0, v));
const ioCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const ioSine = (t) => -(Math.cos(Math.PI * t) - 1) / 2;
const ramp = (v, a, b, from, to, ease = ioSine) => from + (to - from) * ease(clamp01((v - a) / (b - a)));

const useLayout = () => {
  const [L, setL] = useState(null);
  const lastW = useRef(0);
  useEffect(() => {
    const calc = () => {
      const vw = window.innerWidth;
      lastW.current = vw;
      const vh = window.innerHeight;
      const mobile = vw < 768;
      const pkgW = mobile ? vw * 1.3 : Math.min(vw * 0.8, vh * 1.18, 980);
      const pkgH = pkgW * PKG_RATIO;
      const pkgLeft = mobile ? -vw * 0.45 : (vw - pkgW) / 2 - vw * 0.05;
      const pkgTop = mobile ? vh * 0.5 - pkgH * LABEL.y : (vh - pkgH) / 2 + vh * 0.03;
      const S1 = pkgW * LABEL.w;
      const small = mobile ? Math.min(vw * 0.82, vh * 0.42) : Math.min(vh * 0.62, vw * 0.4);
      const big = mobile ? Math.min(vw * 1.15, vh * 0.62) : Math.min(vh * 0.98, vw * 0.62);
      const mid = mobile ? Math.min(vw * 0.6, vh * 0.3) : Math.min(vh * 0.5, 460);
      const center = { x: vw * 0.5, y: vh * 0.5 };
      const spinY = vh * 0.5;
      const end = { x: pkgLeft + pkgW * LABEL.x, y: pkgTop + pkgH * LABEL.y };
      const discR = (S1 * 1.1 * DISC) / 2;
      const waitY = vh - discR + 6;
      const offHold = vh - end.y + discR + 10;
      setL({ vw, vh, mobile, pkgW, pkgH, pkgLeft, pkgTop, S1, small, big, mid, center, spinY, end, waitY, offHold });
    };
    calc();
    const onResize = () => {
      if (window.innerWidth < 768 && lastW.current === window.innerWidth) return;
      calc();
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return L;
};

export const HeroText = () => (
  <section data-testid="hero-text-section" className="bg-[#F2F3F5] text-[#0D1B2A] pt-16 md:pt-24 pb-28 md:pb-44 px-6 md:px-10">
    <div className="grid grid-cols-12 gap-x-6 gap-y-14 items-end">
      <div className="col-span-12 md:col-span-7">
        <Lines lines={["The date on", "the package", "is a guess."]} as="h1" className="display text-[2.6rem] sm:text-6xl lg:text-[5.4rem]" testId="hero-headline" />
      </div>
      <div className="col-span-12 md:col-span-4 md:col-start-9">
        <Fade delay={0.3} testId="hero-copy">
          <p className="text-lg md:text-xl font-medium tracking-tight">
            Telltag is a multilayer indicator label that detects the volatile basic compounds released by meat during
            microbial spoilage and translates their concentration into a colorimetric signal on the package.
          </p>
          <p className="body-copy mt-5">
            A printed date is an estimate fixed before the package leaves the plant. Telltag is a continuous
            measurement taken at the food itself, with no contact between indicator chemistry and product. The
            indicator remains purple while the meat is fresh and shifts to yellow as spoilage compounds accumulate.
          </p>
          <p className="text-lg md:text-xl font-medium tracking-tight mt-5">
            The objective: less edible food discarded, and a lower risk of consuming food that has spoiled.
          </p>
          <a
            href="#science"
            onClick={(e) => {
              e.preventDefault();
              window.dispatchEvent(new CustomEvent("telltag:topic", { detail: 1 }));
              document.getElementById("science")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="inline-flex items-center gap-3 mt-8 py-3 eyebrow text-[#0D1B2A] underline underline-offset-4 decoration-[#0D1B2A]/30 hover:decoration-[#0D1B2A] transition-colors"
            data-testid="hero-chemistry-link"
          >
            See the chemistry
            <span aria-hidden>↓</span>
          </a>
        </Fade>
      </div>
    </div>
  </section>
);

export const Hero = () => {
  const ref = useRef(null);
  const L = useLayout();
  const { scrollYProgress: p } = useScroll({ target: ref, offset: ["start start", "end end"] });

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const smx = useSpring(mx, { stiffness: 60, damping: 20 });
  const smy = useSpring(my, { stiffness: 60, damping: 20 });

  useEffect(() => {
    if (!window.matchMedia("(pointer: fine)").matches) return;
    const onMove = (e) => {
      mx.set((e.clientX / window.innerWidth - 0.5) * 2);
      my.set((e.clientY / window.innerHeight - 0.5) * 2);
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [mx, my]);

  // package: rises to a waiting position (label still below the edge), holds, then rises to its final place
  const pkgOff = (v) => {
    if (!L) return 0;
    if (v < 0.62) return ramp(v, 0.5, 0.62, L.vh * 0.95, L.offHold, ioSine);
    if (v < 0.72) return L.offHold;
    return ramp(v, 0.72, 0.86, L.offHold, 0, ioSine);
  };
  // sticker: grows toward the viewer, spins, shrinks, travels down to meet the package, rides with it, presses
  const GROW = [0.03, 0.2];
  const SPIN = [0.18, 0.52];
  const SHRINK = [0.44, 0.62];
  const TRAVEL = [0.62, 0.75];
  const spinT = (v) => clamp01((v - SPIN[0]) / (SPIN[1] - SPIN[0]));
  const rest = (v) => 1 - clamp01((v - GROW[0]) / (GROW[1] - GROW[0]));
  const press = (v) => 1 + 0.1 * (1 - ramp(v, 0.75, 0.81, 0, 1));
  const S = (v) => {
    if (!L) return 0;
    if (v < SHRINK[0]) return ramp(v, GROW[0], GROW[1], L.small, L.big, ioCubic);
    if (v < TRAVEL[0]) return ramp(v, SHRINK[0], SHRINK[1], L.big, L.mid, ioCubic);
    return ramp(v, TRAVEL[0], TRAVEL[1], L.mid, L.S1 * 1.1, ioCubic) * press(v);
  };
  const centerX = (v) => {
    if (!L) return 0;
    const sway = Math.sin(spinT(v) * Math.PI * 2) * L.vw * 0.025;
    return ramp(v, TRAVEL[0], TRAVEL[1], L.center.x, L.end.x, ioCubic) + sway;
  };
  const centerY = (v) => {
    if (!L) return 0;
    const lift = -Math.sin(spinT(v) * Math.PI) * L.vh * 0.03;
    if (v < TRAVEL[0]) return L.center.y + lift;
    const travel = ramp(v, TRAVEL[0], TRAVEL[1], L.center.y, L.waitY, ioCubic);
    return Math.min(travel, L.end.y + pkgOff(v));
  };

  const stickerW = useTransform(p, (v) => S(v));
  const stickerX = useTransform(p, (v) => centerX(v) - S(v) / 2);
  const stickerY = useTransform(p, (v) => centerY(v) - S(v) / 2);
  const rotY = useTransform([p, smx], ([v, m]) => -14 * rest(v) + ramp(v, SPIN[0], SPIN[1], 0, 360, ioCubic) + m * 6 * rest(v));
  const rotX = useTransform(
    [p, smy],
    ([v, m]) => 8 * rest(v) + Math.sin(spinT(v) * Math.PI) * 10 + ramp(v, TRAVEL[0], TRAVEL[1], 0, 22, ioSine) - m * 5 * rest(v)
  );
  const rotZ = useTransform(p, (v) => Math.sin(spinT(v) * Math.PI) * -6 + ramp(v, TRAVEL[0], TRAVEL[1], 0, -6));
  const stickerOpacity = useTransform(p, (v) => ramp(v, 0.87, 0.91, 1, 0));

  const pkgY = useTransform(p, (v) => pkgOff(v));
  const pkgOpacity = useTransform(p, (v) => ramp(v, 0.5, 0.56, 0, 1));
  const bg = useTransform(p, [0.46, 0.56], ["#0D1B2A", "#F2F3F5"]);

  const hintOpacity = useTransform(p, (v) => ramp(v, 0.0, 0.05, 1, 0));

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative on-dark h-[360vh] md:h-[520vh]">
      <motion.div className="sticky top-0 h-[100svh] w-full overflow-hidden" style={{ background: bg, perspective: 1400 }}>
        {/* Scroll hint */}
        <motion.div
          className="absolute left-1/2 -translate-x-1/2 bottom-[6vh] text-[#F2F3F5]/70"
          style={{ opacity: hintOpacity }}
          data-testid="hero-scroll-hint"
        >
          <motion.div
            className="flex items-center gap-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.2, duration: 1 }}
          >
            <span className="eyebrow">Scroll</span>
            <span className="block h-px w-12 bg-current" />
          </motion.div>
        </motion.div>

        {/* Package */}
        {L && (
          <motion.img
            src="/assets/beef-package.webp"
            alt="Packaged meat with Telltag freshness indicator attached"
            data-testid="hero-package"
            className="absolute max-w-none pointer-events-none"
            style={{ left: L.pkgLeft, top: L.pkgTop, width: L.pkgW, y: pkgY, opacity: pkgOpacity }}
            fetchPriority="high"
          />
        )}

        {/* Physical sticker */}
        {L && (
          <motion.div
            data-testid="hero-sticker"
            className="absolute aspect-square"
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 2, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            style={{ width: stickerW, x: stickerX, y: stickerY, transformStyle: "preserve-3d" }}
          >
            <motion.div
              className="relative w-full h-full"
              style={{ rotateY: rotY, rotateX: rotX, rotateZ: rotZ, transformStyle: "preserve-3d", opacity: stickerOpacity }}
            >
              {(L.mobile ? SLICES_M : SLICES).map((z) => (
                <div
                  key={z}
                  aria-hidden
                  className="absolute rounded-full"
                  style={{ left: "18.2%", top: "15.7%", width: "63.6%", height: "66.3%", background: "#DAD6CC", transform: `translateZ(${z}px)` }}
                />
              ))}
              <div className="absolute inset-0" style={{ backfaceVisibility: "hidden", transform: `translateZ(${T + 0.5}px)` }}>
                <img src="/assets/sticker.webp" alt="Telltag freshness indicator" className="w-full h-full block" fetchPriority="high" />
              </div>
              <div className="absolute inset-0" style={{ backfaceVisibility: "hidden", transform: `rotateY(180deg) translateZ(${T + 0.5}px)` }} aria-hidden>
                <div
                  className="absolute rounded-full"
                  style={{ left: "18.2%", top: "15.7%", width: "63.6%", height: "66.3%", background: "#E6E3DC", boxShadow: "inset 0 0 0 1px #D3CFC5" }}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </motion.div>
    </section>
  );
};
